import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import DateRangeFilter, { getDateRange } from '../../../components/DateRangeFilter';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d?.getTime())) return '—';
  return d?.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
};

const formatCurrency = (val) =>
  '₹' + Number(val || 0)?.toLocaleString('en-IN');

// Dot progress bar: filled green dots for assigned, empty gray for pending
const TruckProgressDots = ({ assigned, total }) => {
  const dots = [];
  for (let i = 0; i < total; i++) {
    dots?.push(
      <span
        key={i}
        className="inline-block w-3 h-3 rounded-full border"
        style={{
          background: i < assigned ? '#16a34a' : 'transparent',
          borderColor: i < assigned ? '#16a34a' : '#9ca3af',
        }}
      />
    );
  }
  return <div className="flex items-center gap-1 flex-wrap">{dots}</div>;
};

// Modal showing all assigned vehicles and pending trucks
const TruckDetailsModal = ({ bundle, onClose }) => {
  if (!bundle) return null;
  const assignments = bundle?.truckAssignments || [];
  const totalTrucks = bundle?.noOfTrucks || 0;
  const assignedCount = assignments?.length;
  const pendingCount = Math.max(0, totalTrucks - assignedCount);

  const getTruckStatusStyle = (status) => {
    const styles = {
      'assigned': { bg: '#fef3c7', text: '#92400e' },
      'in_transit': { bg: '#dbeafe', text: '#1d4ed8' },
      'delivered': { bg: '#dcfce7', text: '#166534' },
    };
    return styles?.[status] || { bg: '#f3f4f6', text: '#6b7280' };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 z-10"
          style={{ borderColor: '#e8ddd0', background: '#fff3e8' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#fff3e8', border: '1px solid #fde68a' }}>
              <Icon name="Truck" size={18} color="#f97316" />
            </div>
            <div>
              <h3 className="font-bold text-base" style={{ color: '#1e293b' }}>Truck Assignment Details</h3>
              <p className="text-xs" style={{ color: '#9ca3af' }}>
                {bundle?.readableBookingId || bundle?.readableOfferId || bundle?.poNumber} · {bundle?.millName}
              </p>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-orange-100 transition-colors">
            <Icon name="X" size={18} color="#6b7280" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Summary bar */}
          <div className="rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4"
            style={{ background: '#ffffff', border: '1px solid #e8ddd0' }}>
            <div className="flex-1">
              <p className="text-sm font-bold mb-2" style={{ color: '#1e293b' }}>
                {assignedCount}/{totalTrucks} Trucks Assigned
              </p>
              <TruckProgressDots assigned={assignedCount} total={totalTrucks} />
              <p className="text-xs mt-2" style={{ color: '#6b7280' }}>
                <span className="font-semibold" style={{ color: '#16a34a' }}>{assignedCount} Assigned</span>
                {pendingCount > 0 && (
                  <> · <span className="font-semibold" style={{ color: '#d97706' }}>{pendingCount} Pending Assignment</span></>
                )}
              </p>
            </div>
            <div className="flex gap-3 text-center">
              <div className="rounded-lg px-4 py-2" style={{ background: '#dcfce7' }}>
                <p className="text-lg font-bold" style={{ color: '#16a34a' }}>{assignedCount}</p>
                <p className="text-xs" style={{ color: '#166534' }}>Assigned</p>
              </div>
              <div className="rounded-lg px-4 py-2" style={{ background: '#fef3c7' }}>
                <p className="text-lg font-bold" style={{ color: '#d97706' }}>{pendingCount}</p>
                <p className="text-xs" style={{ color: '#92400e' }}>Pending</p>
              </div>
            </div>
          </div>

          {/* Assigned Vehicles */}
          {assignedCount > 0 && (
            <div>
              <h4 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1e293b' }}>
                <Icon name="CheckCircle" size={15} color="#16a34a" />
                Assigned Vehicles ({assignedCount})
              </h4>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto rounded-xl" style={{ border: '1px solid #e8ddd0' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ background: '#fef3c7' }}>
                      {['#', 'Vehicle No.', 'Driver', 'Phone', 'Qty (MT)', 'Rate/Ton', 'Freight', 'Destination', 'Status']?.map(col => (
                        <th key={col} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap"
                          style={{ color: '#92400e', borderBottom: '1px solid #fde68a' }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {assignments?.map((t, ti) => {
                      const freight = Number(t?.rate_per_ton || 0) * Number(t?.assigned_quantity || 0);
                      const tStyle = getTruckStatusStyle(t?.status);
                      return (
                        <tr key={t?.id}
                          style={{ background: ti % 2 === 0 ? '#ffffff' : '#fffdf7', borderBottom: '1px solid #fef3c7' }}>
                          <td className="px-3 py-2.5 font-bold" style={{ color: '#d97706' }}>{ti + 1}</td>
                          <td className="px-3 py-2.5 font-semibold whitespace-nowrap" style={{ color: '#1e293b' }}>
                            {t?.vehicle_number || '—'}
                          </td>
                          <td className="px-3 py-2.5" style={{ color: '#374151' }}>{t?.driver_name || '—'}</td>
                          <td className="px-3 py-2.5" style={{ color: '#374151' }}>{t?.driver_phone || '—'}</td>
                          <td className="px-3 py-2.5 font-semibold text-center" style={{ color: '#1e293b' }}>
                            {Number(t?.assigned_quantity)?.toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 font-medium" style={{ color: '#374151' }}>
                            ₹{Number(t?.rate_per_ton)?.toLocaleString('en-IN')}
                          </td>
                          <td className="px-3 py-2.5 font-semibold whitespace-nowrap" style={{ color: '#059669' }}>
                            {formatCurrency(freight)}
                          </td>
                          <td className="px-3 py-2.5" style={{ color: '#374151' }}>{t?.destination || '—'}</td>
                          <td className="px-3 py-2.5">
                            <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                              style={{ background: tStyle?.bg, color: tStyle?.text }}>
                              {t?.status || 'assigned'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#fef9c3', borderTop: '2px solid #fde68a' }}>
                      <td colSpan={4} className="px-3 py-2.5 font-bold text-xs" style={{ color: '#92400e' }}>Total</td>
                      <td className="px-3 py-2.5 font-bold text-center" style={{ color: '#1e293b' }}>
                        {assignments?.reduce((s, t) => s + Number(t?.assigned_quantity || 0), 0)?.toLocaleString('en-IN')} MT
                      </td>
                      <td />
                      <td className="px-3 py-2.5 font-bold" style={{ color: '#059669' }}>
                        {formatCurrency(assignments?.reduce((s, t) => s + (Number(t?.rate_per_ton || 0) * Number(t?.assigned_quantity || 0)), 0))}
                      </td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="md:hidden space-y-3">
                {assignments?.map((t, ti) => {
                  const freight = Number(t?.rate_per_ton || 0) * Number(t?.assigned_quantity || 0);
                  const tStyle = getTruckStatusStyle(t?.status);
                  return (
                    <div key={t?.id} className="rounded-xl p-3"
                      style={{ background: '#ffffff', border: '1px solid #e8ddd0' }}>
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold text-sm" style={{ color: '#f97316' }}>
                          Truck {ti + 1}: {t?.vehicle_number || '—'}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{ background: tStyle?.bg, color: tStyle?.text }}>
                          {t?.status || 'assigned'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span style={{ color: '#9ca3af' }}>Driver: </span><span style={{ color: '#374151' }}>{t?.driver_name || '—'}</span></div>
                        <div><span style={{ color: '#9ca3af' }}>Phone: </span><span style={{ color: '#374151' }}>{t?.driver_phone || '—'}</span></div>
                        <div><span style={{ color: '#9ca3af' }}>Qty: </span><span className="font-semibold" style={{ color: '#1e293b' }}>{Number(t?.assigned_quantity)?.toLocaleString('en-IN')} MT</span></div>
                        <div><span style={{ color: '#9ca3af' }}>Rate: </span><span style={{ color: '#374151' }}>₹{Number(t?.rate_per_ton)?.toLocaleString('en-IN')}/ton</span></div>
                        <div className="col-span-2"><span style={{ color: '#9ca3af' }}>Freight: </span><span className="font-bold" style={{ color: '#059669' }}>{formatCurrency(freight)}</span></div>
                        {t?.destination && <div className="col-span-2"><span style={{ color: '#9ca3af' }}>Dest: </span><span style={{ color: '#374151' }}>{t?.destination}</span></div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pending Trucks */}
          {pendingCount > 0 && (
            <div>
              <h4 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#92400e' }}>
                <Icon name="Clock" size={15} color="#d97706" />
                Pending Assignment ({pendingCount} truck{pendingCount !== 1 ? 's' : ''})
              </h4>
              <div className="space-y-2">
                {Array.from({ length: pendingCount })?.map((_, i) => (
                  <div key={i} className="rounded-xl p-3 flex items-center gap-3"
                    style={{ background: '#fffbeb', border: '1px dashed #fde68a' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#fef3c7' }}>
                      <Icon name="Truck" size={16} color="#d97706" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold" style={{ color: '#92400e' }}>
                        Truck {assignedCount + i + 1} — Awaiting Assignment
                      </p>
                      <p className="text-xs" style={{ color: '#b45309' }}>
                        The Quota Manager will assign a vehicle shortly
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-5 pb-5">
          <button onClick={onClose}
            className="w-full py-2.5 rounded-xl text-sm font-bold transition-all"
            style={{ background: '#f97316', color: '#ffffff', border: '1px solid #ea580c' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const SugarTruckManagementTab = ({ onSwitchToTracking }) => {
  const { user } = useAuth();
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [preset, setPreset] = useState('this_fy');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionNotification, setActionNotification] = useState({ show: false, message: '', type: '' });
  const [detailsBundle, setDetailsBundle] = useState(null);

  const loadBundles = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const userEmail = session?.user?.email || user?.email;
      const userId = session?.user?.id || user?.id;

      if (!userEmail && !userId) {
        setBundles([]);
        return;
      }

      const { data, error } = await supabase
        ?.from('buyer_bookings')
        ?.select('*, readable_offer_id, readable_booking_id')
        ?.in('status', ['approved', 'confirmed', 'active', 'in_transit', 'do_generated'])
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Filter to current buyer
      const filtered = (data || [])?.filter(b => {
        const isBuyer = b?.buyer_email === userEmail || b?.buyer_id === userId;
        const bookingType = (b?.booking_type || '')?.toLowerCase();
        const isBundle =
          bookingType?.includes('sugar + vehicle bundle') ||
          bookingType?.includes('bundle') ||
          bookingType?.includes('sugar_truck') ||
          bookingType?.includes('sugar+truck') ||
          bookingType?.includes('sugar + truck');
        return isBuyer && isBundle;
      });

      if (filtered?.length === 0) {
        setBundles([]);
        return;
      }

      // Fetch truck assignments for all matching bookings
      const bookingIds = filtered?.map(b => b?.id);
      const { data: assignments, error: assignError } = await supabase
        ?.from('order_truck_assignments')
        ?.select('*')
        ?.in('booking_id', bookingIds)
        ?.order('assigned_at', { ascending: true });

      if (assignError) {
        console.error('order_truck_assignments fetch error:', assignError?.message);
      }

      // Group assignments by booking_id
      const assignmentsByBooking = {};
      (assignments || [])?.forEach(a => {
        const key = a?.booking_id;
        if (!assignmentsByBooking?.[key]) assignmentsByBooking[key] = [];
        assignmentsByBooking?.[key]?.push(a);
      });

      const formatted = filtered?.map(b => ({
        id: b?.id,
        millName: b?.factory_name || b?.mill_name || '—',
        grade: b?.grade || '—',
        quantity: Number(b?.quantity) || 0,
        totalAmount: Number(b?.total_amount) || 0,
        noOfTrucks: b?.no_of_trucks || 1,
        from: b?.loading_from || '—',
        to: b?.destination || '—',
        status: b?.delivery_status || b?.status || 'Processing',
        progress: b?.progress || (
          b?.status === 'in_transit' ? 60 :
          b?.status === 'active' ? 40 :
          b?.status === 'do_generated' ? 20 :
          b?.status === 'approved' ? 15 : 10
        ),
        estimatedArrival: b?.estimated_delivery,
        date: b?.booking_date || b?.created_at || new Date()?.toISOString(),
        bookingType: b?.booking_type || '—',
        readableOfferId: b?.readable_offer_id || null,
        readableBookingId: b?.readable_booking_id || null,
        poNumber: b?.readable_offer_id || b?.readable_booking_id || b?.offer_id || b?.id?.toString()?.slice(0, 8)?.toUpperCase(),
        assignedVehicle: b?.assigned_vehicle_number || '—',
        transporterRate: b?.transporter_rate_per_ton || 0,
        truckAssignments: assignmentsByBooking?.[b?.id] || [],
      }));

      setBundles(formatted || []);
    } catch (err) {
      console.error('SugarTruckManagementTab error:', err?.message);
      setBundles([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadBundles();

    const channel = supabase?.channel('sugar_truck_bundles_tab')
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'buyer_bookings'
      }, () => loadBundles())
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'order_truck_assignments'
      }, () => loadBundles())
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [loadBundles]);

  const statusOptions = [
    { value: 'all', label: 'All Active' },
    { value: 'approved', label: 'Approved' },
    { value: 'active', label: 'Active' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'do_generated', label: 'DO Generated' },
  ];

  const getStatusStyle = (status) => {
    const styles = {
      'In Transit': { bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6' },
      'in_transit': { bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6' },
      'Loading': { bg: '#fef3c7', text: '#d97706', dot: '#f59e0b' },
      'Dispatched': { bg: '#ede9fe', text: '#7c3aed', dot: '#8b5cf6' },
      'dispatched': { bg: '#ede9fe', text: '#7c3aed', dot: '#8b5cf6' },
      'approved': { bg: '#dcfce7', text: '#16a34a', dot: '#22c55e' },
      'confirmed': { bg: '#dbeafe', text: '#1d4ed8', dot: '#3b82f6' },
      'active': { bg: '#d1fae5', text: '#065f46', dot: '#10b981' },
      'Processing': { bg: '#fef9c3', text: '#ca8a04', dot: '#eab308' },
      'do_generated': { bg: '#f3e8ff', text: '#7c3aed', dot: '#a855f7' },
    };
    return styles?.[status] || { bg: '#f3f4f6', text: '#6b7280', dot: '#9ca3af' };
  };

  // Filter: status + search + date
  const { from: dateFrom, to: dateTo } = getDateRange(preset, customFrom, customTo);

  const filteredBundles = bundles?.filter(b => {
    // Status filter
    if (filterStatus !== 'all') {
      const statusMatch =
        b?.status?.toLowerCase()?.replace(/\s+/g, '_') === filterStatus ||
        b?.status?.toLowerCase() === filterStatus;
      if (!statusMatch) return false;
    }

    // Search filter
    const s = searchText?.toLowerCase() || '';
    if (s) {
      const vehicleMatch =
        b?.truckAssignments?.some(t => t?.vehicle_number?.toLowerCase()?.includes(s)) ||
        (b?.assignedVehicle && b?.assignedVehicle !== '—' && b?.assignedVehicle?.toLowerCase()?.includes(s));
      const matchesSearch =
        b?.millName?.toLowerCase()?.includes(s) ||
        b?.grade?.toLowerCase()?.includes(s) ||
        b?.readableBookingId?.toLowerCase()?.includes(s) ||
        b?.readableOfferId?.toLowerCase()?.includes(s) ||
        b?.poNumber?.toLowerCase()?.includes(s) ||
        vehicleMatch;
      if (!matchesSearch) return false;
    }

    // Date filter (on bundle.date = booking_date || created_at)
    const bundleDate = b?.date ? new Date(b.date) : null;
    const matchesDate =
      (!dateFrom && !dateTo) ||
      (bundleDate &&
        (!dateFrom || bundleDate >= dateFrom) &&
        (!dateTo || bundleDate <= dateTo));
    if (!matchesDate) return false;

    return true;
  });

  const showNotif = (msg) => {
    setActionNotification({ show: true, message: msg, type: 'success' });
    setTimeout(() => setActionNotification({ show: false, message: '', type: '' }), 3000);
  };

  const handleOpenTracking = (bundle) => {
    onSwitchToTracking(bundle?.id);
  };

  return (
    <div className="space-y-6">

      {/* Truck Details Modal */}
      {detailsBundle && (
        <TruckDetailsModal
          bundle={detailsBundle}
          onClose={() => setDetailsBundle(null)}
        />
      )}

      {/* Notification */}
      {actionNotification?.show && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-lg shadow-xl text-sm font-medium text-white"
          style={{ background: '#059669', border: '1px solid #047857' }}>
          <Icon name="CheckCircle" size={18} color="#ffffff" />
          {actionNotification?.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: '#1e293b' }}>Sugar + Truck Management</h2>
          <p className="text-sm mt-0.5" style={{ color: '#374151' }}>
            {loading ? 'Loading...' : `${filteredBundles?.length} active bundle${filteredBundles?.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {statusOptions?.map(opt => (
            <button
              key={opt?.value}
              onClick={() => setFilterStatus(opt?.value)}
              className="px-3 py-1.5 rounded-md text-xs font-semibold transition-all"
              style={{
                background: filterStatus === opt?.value ? '#f97316' : '#e5e7eb',
                color: filterStatus === opt?.value ? '#ffffff' : '#374151',
                border: `1px solid ${filterStatus === opt?.value ? '#ea580c' : '#d1d5db'}`,
              }}
            >
              {opt?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search + Date Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Icon name="Search" size={16} color="#9ca3af" />
          </span>
          <input
            type="text"
            placeholder="Search by mill, grade, booking ID, vehicle..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
            style={{ border: '1px solid #e8ddd0', color: '#1e293b', backgroundColor: '#ffffff' }}
          />
        </div>
        <DateRangeFilter
          preset={preset}
          onPresetChange={setPreset}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-12 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm" style={{ color: '#9ca3af' }}>Loading Sugar + Truck bundles...</p>
        </div>
      )}

      {/* Empty State — no data at all */}
      {!loading && bundles?.length === 0 && (
        <div className="text-center py-16 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <Icon name="Package" size={40} color="#d1a87a" />
          <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>No active Sugar + Truck bundles</p>
          <p className="text-xs mt-1" style={{ color: '#d1a87a' }}>
            Approved Sugar + Truck orders will appear here
          </p>
        </div>
      )}

      {/* Empty State — data exists but no match */}
      {!loading && bundles?.length > 0 && filteredBundles?.length === 0 && (
        <div className="text-center py-16 rounded-xl" style={{ background: '#fdf8f0', border: '1px solid #e8ddd0' }}>
          <Icon name="Search" size={40} color="#d1a87a" />
          <p className="mt-3 text-sm font-medium" style={{ color: '#9ca3af' }}>No matching bundles</p>
          <p className="text-xs mt-1" style={{ color: '#d1a87a' }}>Try adjusting your search or date filter</p>
        </div>
      )}

      {/* Bundle Cards */}
      {!loading && filteredBundles?.length > 0 && (
        <div className="space-y-4">
          {filteredBundles?.map((bundle, idx) => {
            const statusStyle = getStatusStyle(bundle?.status);
            const isExpanded = expandedId === bundle?.id;
            const assignedCount = bundle?.truckAssignments?.length;
            const totalTrucks = bundle?.noOfTrucks || 0;
            const pendingCount = Math.max(0, totalTrucks - assignedCount);
            const hasTrucks = assignedCount > 0;
            const totalFreight = bundle?.truckAssignments?.reduce(
              (s, t) => s + (Number(t?.rate_per_ton || 0) * Number(t?.assigned_quantity || 0)), 0
            );

            return (
              <div
                key={bundle?.id}
                className="rounded-xl transition-all duration-200"
                style={{
                  background: idx % 2 === 0 ? '#ffffff' : '#fdf8f0',
                  border: '1px solid #e8ddd0',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}
              >
                {/* Card Header */}
                <div className="p-5">
                  {/* Top Row */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: '#fff3e8' }}>
                        <Icon name="Truck" size={20} color="#f97316" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-base leading-tight" style={{ color: '#1e293b' }}>
                          {bundle?.millName}
                        </h3>
                        <p className="text-xs mt-0.5" style={{ color: '#9ca3af' }}>
                          Ref: {bundle?.readableBookingId || bundle?.readableOfferId || bundle?.poNumber} &nbsp;·&nbsp; {formatDate(bundle?.date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                        style={{ background: statusStyle?.bg, color: statusStyle?.text }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusStyle?.dot }} />
                        {bundle?.status}
                      </span>
                    </div>
                  </div>

                  {/* ── Dynamic Truck Assignment Display ── */}
                  <div className="rounded-xl p-4 mb-4" style={{ background: '#f8f4ee', border: '1px solid #e8ddd0' }}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-bold mb-2" style={{ color: '#1e293b' }}>
                          {assignedCount}/{totalTrucks} Trucks Assigned
                        </p>
                        <TruckProgressDots assigned={assignedCount} total={totalTrucks} />
                        <p className="text-xs mt-2" style={{ color: '#6b7280' }}>
                          <span className="font-semibold" style={{ color: '#16a34a' }}>{assignedCount} Assigned</span>
                          {pendingCount > 0 && (
                            <> &nbsp;·&nbsp; <span className="font-semibold" style={{ color: '#d97706' }}>{pendingCount} Pending Assignment</span></>
                          )}
                        </p>
                      </div>
                      {hasTrucks && (
                        <div className="flex gap-2 text-center">
                          <div className="rounded-lg px-3 py-2" style={{ background: '#dcfce7' }}>
                            <p className="text-base font-bold" style={{ color: '#16a34a' }}>{assignedCount}</p>
                            <p className="text-xs" style={{ color: '#166534' }}>Assigned</p>
                          </div>
                          {pendingCount > 0 && (
                            <div className="rounded-lg px-3 py-2" style={{ background: '#fef3c7' }}>
                              <p className="text-base font-bold" style={{ color: '#d97706' }}>{pendingCount}</p>
                              <p className="text-xs" style={{ color: '#92400e' }}>Pending</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                    <div className="rounded-lg px-3 py-2.5" style={{ background: '#f3ede3' }}>
                      <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>Grade</p>
                      <p className="text-sm font-medium" style={{ color: '#374151' }}>{bundle?.grade}</p>
                    </div>
                    <div className="rounded-lg px-3 py-2.5" style={{ background: '#f3ede3' }}>
                      <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>Quantity</p>
                      <p className="text-sm font-semibold" style={{ color: '#1e293b' }}>{bundle?.quantity} MT</p>
                    </div>
                    <div className="rounded-lg px-3 py-2.5" style={{ background: '#f3ede3' }}>
                      <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>Trucks</p>
                      <p className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                        {assignedCount}/{totalTrucks}
                      </p>
                    </div>
                    <div className="rounded-lg px-3 py-2.5" style={{ background: '#f3ede3' }}>
                      <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>Vehicle No.</p>
                      <p className="text-sm font-medium" style={{ color: '#374151' }}>
                        {hasTrucks
                          ? assignedCount === 1
                            ? bundle?.truckAssignments?.[0]?.vehicle_number || '—'
                            : `${bundle?.truckAssignments?.[0]?.vehicle_number} +${assignedCount - 1}`
                          : bundle?.assignedVehicle !== '—' ? bundle?.assignedVehicle : '—'}
                      </p>
                    </div>
                    <div className="rounded-lg px-3 py-2.5" style={{ background: '#f3ede3' }}>
                      <p className="text-xs mb-1" style={{ color: '#9ca3af' }}>Total Amount</p>
                      <p className="text-sm font-semibold" style={{ color: '#d97706' }}>
                        {formatCurrency(bundle?.totalAmount)}
                      </p>
                    </div>
                  </div>

                  {/* Route */}
                  <div className="flex items-center gap-2 mb-4 text-xs" style={{ color: '#9ca3af' }}>
                    <Icon name="MapPin" size={13} color="#9ca3af" />
                    <span>{bundle?.from}</span>
                    <Icon name="ArrowRight" size={13} color="#9ca3af" />
                    <span>{bundle?.to}</span>
                    {bundle?.estimatedArrival && (
                      <>
                        <span className="ml-2">·</span>
                        <Icon name="Calendar" size={13} color="#9ca3af" />
                        <span>Est: {formatDate(bundle?.estimatedArrival)}</span>
                      </>
                    )}
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1" style={{ color: '#9ca3af' }}>
                      <span>Journey Progress</span>
                      <span>{bundle?.progress}%</span>
                    </div>
                    <div className="w-full h-2 rounded-full" style={{ background: '#e5e7eb' }}>
                      <div
                        className="h-2 rounded-full transition-all duration-500"
                        style={{ width: `${bundle?.progress}%`, background: '#f97316' }}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-2 pt-1 border-t" style={{ borderColor: '#e8ddd0' }}>
                    <button
                      onClick={() => setDetailsBundle(bundle)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                      style={{ background: '#f97316', color: '#ffffff', border: '1px solid #ea580c' }}
                    >
                      <Icon name="Eye" size={14} />
                      View Details
                    </button>

                    {!hasTrucks && (
                      <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold"
                        style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                        <Icon name="Clock" size={13} color="#d97706" />
                        Trucks pending assignment
                      </span>
                    )}
                    <button
                      onClick={() => handleOpenTracking(bundle)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-90"
                      style={{ background: '#2563eb', color: '#ffffff', border: '1px solid #1d4ed8' }}
                    >
                      <Icon name="Navigation" size={14} />
                      Track
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SugarTruckManagementTab;
